import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import cn from 'classnames';
import { isAfter, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { InvoicePaymentFailureCode } from 'anchor-scout-common/types';
import { Button, Callout, DropdownMenu, DropdownMenuItem, Typography } from 'seagull';
import { ChevronDown, Refresh } from 'seagull/icons/arrows';
import { Send } from 'seagull/icons/emails-messages';
import { LinkIcon } from 'seagull/icons/essentials';
import { ShareByLinkDialog } from '~/components/AdHocBillModal/ConfirmationDialogs/ShareByLinkDialog';
import { routes } from '~/constants';
import { useOverlayContext, useStoreActions, useStore, useIoContext } from '~/contexts';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { invoiceModel, relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import ChangesApprovalInfoBox from '../ChangesApprovalInfoBox/ChangesApprovalInfoBox';
import ExternalLinkButton from '../ExternalLinkButton';
import styles from './FullPageInvoice.module.scss';
export const calloutTextsPath = 'components.fullPageInvoice.callout';
export function FullPageInvoiceCallout(props) {
    const { id, text, breakOnNewLines, intent, actions, actionSlot, footer } = props;
    return /*#__PURE__*/ _jsx(Callout, {
        intent: intent,
        testId: "fpi-".concat(id),
        footer: footer,
        withAction: actionSlot || !!(actions === null || actions === void 0 ? void 0 : actions.length) && /*#__PURE__*/ _jsx(_Fragment, {
            children: actions.map((a)=>/*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'invoice_callout_action',
                                data: {
                                    callout_id: id
                                }
                            },
                            intent: a.intent,
                            skin: a.skin,
                            disabled: a.disabled,
                            tooltip: a.tooltipTitle ? {
                                title: /*#__PURE__*/ _jsx("div", {
                                    "data-testid": "fpi-callout-tooltip",
                                    children: a.tooltipTitle
                                }),
                                placement: 'top'
                            } : undefined,
                            loading: a.loading,
                            testId: "fpi-".concat(a.id),
                            onClick: a.onClick,
                            children: a.text
                        }),
                        a.errorMsg && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "error",
                            testId: "fpi-".concat(a.id, "-error"),
                            children: a.errorMsg
                        })
                    ]
                }, id))
        }),
        children: /*#__PURE__*/ _jsx("div", {
            className: cn(breakOnNewLines && styles.breakOnNewLines),
            children: text
        })
    });
}
function showLearnAboutHandlingChargebacks(invoice, isVendor) {
    const failureCode = invoiceModel.paymentFailureCode(invoice);
    return isVendor && failureCode && [
        InvoicePaymentFailureCode.DisputeProductUnacceptable,
        InvoicePaymentFailureCode.DisputeCreditNotProcessed
    ].includes(failureCode);
}
export function getFullPageInvoiceCallouts(props, calloutTexts) {
    const { invoice, relationship, isVendor, isAdHoc, disabling, now, onPayNow, onResumePayment, onApprovePayment, onDisableAutoApprove } = props;
    const isRefundRequested = invoiceModel.isRefundRequested(invoice);
    const isRefundFailed = invoiceModel.isRefundFailed(invoice);
    const hasPaymentMethod = relationshipModel.paymentMethod(relationship) != null;
    const customerName = relationshipModel.customerName(relationship);
    const sellerName = relationshipModel.sellerFullName(relationship);
    const isTerminated = relationshipModel.isTerminated(relationship);
    const autoApprovalDate = invoiceModel.autoApprovalDate(invoice);
    const paymentProcessingDate = invoiceModel.paymentProcessingDate(invoice);
    const disputedMessage = invoiceModel.disputedMessage(invoice);
    return compact([
        invoiceModel.isPaymentFailed(invoice) && {
            id: 'payment-failed',
            text: /*#__PURE__*/ _jsxs(Typography, {
                testId: "payment-failed-text",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        weight: "normal",
                        children: calloutTexts.paymentFailedLabel()
                    }),
                    ' ',
                    invoiceModel.paymentFailureMessage(invoice) || '',
                    ' ',
                    showLearnAboutHandlingChargebacks(invoice, isVendor) && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                bi: {
                                    name: 'invoice_payment_failed_chargeback_learn_more'
                                },
                                testId: "payment-failed-disputed-learn-more",
                                openInNewTab: true,
                                href: helpArticles.chargebacksAndDisputes('missing-auth'),
                                children: calloutTexts.chargebackInfo()
                            }),
                            ' ',
                            calloutTexts.chargebackSuffix()
                        ]
                    }),
                    isAdHoc && isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            " ",
                            calloutTexts.paymentFailedAdHocVendorInstruction()
                        ]
                    })
                ]
            }),
            intent: 'warning'
        },
        invoiceModel.isRequiresPaymentApproval(invoice) && {
            id: 'requires-approval',
            text: isVendor ? calloutTexts.requiresApprovalVendor() : calloutTexts.requiresApprovalClient(),
            actions: compact([
                !isVendor && {
                    id: 'approve-payment',
                    text: calloutTexts.approvePayment(),
                    intent: Intent.InvoiceApprovePayment,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? calloutTexts.terminated() : undefined,
                    onClick: onApprovePayment
                }
            ]),
            footer: autoApprovalDate && /*#__PURE__*/ _jsx(ChangesApprovalInfoBox, {
                testId: "fpi-auto-approval",
                onDisable: async ()=>onDisableAutoApprove(invoice),
                disabling: disabling,
                autoApprovalDate: autoApprovalDate,
                showDisableButton: !isVendor
            })
        },
        disputedMessage != null && {
            id: 'payment-disputed',
            intent: 'warning',
            text: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        weight: "normal",
                        children: calloutTexts.paymentDisputedLabel()
                    }),
                    ' ',
                    disputedMessage,
                    ' ',
                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                        bi: {
                            name: 'invoice_payment_disputed_learn_more'
                        },
                        testId: "payment-disputed-learn-more",
                        openInNewTab: true,
                        href: helpArticles.chargebacksAndDisputes(),
                        children: calloutTexts.chargebackInfo()
                    }),
                    ' ',
                    calloutTexts.chargebackSuffix()
                ]
            })
        },
        invoiceModel.isPending(invoice) && invoice.netTerms > 0 && {
            id: 'future-payment',
            text: calloutTexts.futurePayment({
                date: paymentProcessingDate
            }),
            actions: compact([
                !isVendor && hasPaymentMethod && isAfter(startOfDay(new Date(paymentProcessingDate)), startOfDay(now)) && {
                    id: 'pay-now',
                    text: calloutTexts.payNow(),
                    intent: Intent.InvoicePayNow,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? calloutTexts.terminated() : undefined,
                    onClick: onPayNow
                }
            ])
        },
        invoiceModel.isPaymentStopped(invoice) && {
            id: 'payment-stopped',
            text: isVendor ? calloutTexts.paymentStoppedVendor({
                customerName,
                reason: invoiceModel.paymentStopReason(invoice)
            }) : calloutTexts.paymentStoppedClient(),
            breakOnNewLines: true,
            intent: isVendor ? 'warning' : 'info',
            actions: compact([
                !isVendor && {
                    id: 'resume-payment',
                    text: calloutTexts.resumePayment(),
                    intent: Intent.InvoiceResumePayment,
                    disabled: isTerminated,
                    tooltipTitle: isTerminated ? calloutTexts.terminated() : undefined,
                    onClick: onResumePayment
                }
            ])
        },
        isRefundRequested && {
            id: 'refund-requested',
            text: calloutTexts.refundRequested(),
            intent: 'warning'
        },
        isRefundFailed && {
            id: 'refund-failed',
            text: calloutTexts.refundFailed(),
            intent: 'warning'
        },
        invoiceModel.isPartiallyRefunded(invoice) && {
            id: 'partially-refunded',
            text: calloutTexts.partiallyRefunded({
                amount: invoiceModel.totalRefunded(invoice),
                feeNote: invoiceModel.hasRefundFees(invoice) ? calloutTexts.partiallyRefundedFees() : '',
                sellerName
            }),
            intent: 'info'
        }
    ]);
}
export function PaymentFailedActions(props) {
    const { invoice, relationship, isVendor, isTerminated, onRetryCharge } = props;
    const { t: calloutTexts } = useLocaleKeys(calloutTextsPath);
    const { isResendingInvoice, resendInvoice } = useResendInvoice();
    const { api, logBI } = useStoreActions();
    const { showModal } = useOverlayContext();
    const retryChargeDisabled = isTerminated || !invoice.isRetryable;
    if (!isVendor) {
        return /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'invoice_payment_failed_client_retry_charge'
            },
            skin: "darkSecondary",
            testId: "fpi-payment-failed-client-retry-charge",
            intent: Intent.InvoiceRetryCharge,
            disabled: retryChargeDisabled,
            tooltip: retryChargeDisabled ? {
                title: /*#__PURE__*/ _jsx("div", {
                    "data-testid": "fpi-callout-tooltip",
                    children: isTerminated ? calloutTexts.terminated() : calloutTexts.contactSupportRetry()
                }),
                placement: 'top'
            } : undefined,
            onClick: ()=>onRetryCharge(),
            children: calloutTexts.retryCharge()
        });
    }
    return /*#__PURE__*/ _jsxs(DropdownMenu, {
        testId: "fpi-payment-failed-actions",
        target: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'invoice_payment_failed_actions'
            },
            skin: "darkSecondary",
            testId: "fpi-payment-failed-actions-trigger",
            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {
                size: 16
            }),
            children: calloutTexts.paymentFailedActions()
        }),
        children: [
            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'invoice_payment_failed_retry_charge'
                },
                testId: "fpi-payment-failed-retry-charge",
                intent: Intent.InvoiceRetryCharge,
                disabled: retryChargeDisabled,
                tooltipProps: retryChargeDisabled ? {
                    title: isTerminated ? calloutTexts.terminated() : calloutTexts.contactSupportRetry(),
                    placement: 'top'
                } : undefined,
                onClick: ()=>onRetryCharge(),
                iconStart: /*#__PURE__*/ _jsx(Refresh, {
                    size: 16
                }),
                children: calloutTexts.retryCharge()
            }),
            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'invoice_payment_failed_send_by_email'
                },
                testId: "fpi-payment-failed-send-by-email",
                intent: Intent.InvoiceResend,
                disabled: isResendingInvoice,
                onClick: ()=>resendInvoice(invoice),
                iconStart: /*#__PURE__*/ _jsx(Send, {
                    size: 16
                }),
                children: calloutTexts.sendInvoiceByEmail()
            }),
            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'invoice_payment_failed_share_by_link'
                },
                intent: Intent.InvoiceCopyLink,
                testId: "fpi-payment-failed-share-by-link",
                onClick: ()=>{
                    logBI(14001);
                    showModal({
                        biName: 'payment_failed_share_by_link_modal',
                        closeOnClickOutside: false,
                        closeOnEscapeKey: false,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(ShareByLinkDialog, {
                                companyName: relationshipModel.customerName(relationship),
                                contactName: relationshipModel.buyerFullName(relationship) || '',
                                email: relationshipModel.buyerEmail(relationship),
                                onCancel: dismissModal,
                                onGenerateLink: async ()=>{
                                    try {
                                        const notificationId = await api.relationship.getDirectLink(relationship.id, {
                                            type: 'invoice',
                                            id: invoice.id
                                        });
                                        const linkUrl = "https://".concat(window.location.host).concat(routes.notification(notificationId));
                                        return {
                                            success: true,
                                            linkUrl
                                        };
                                    } catch (unused) {
                                        return {
                                            success: true,
                                            linkUrl: null
                                        };
                                    }
                                },
                                onClose: dismissModal
                            });
                        }
                    });
                },
                iconStart: /*#__PURE__*/ _jsx(LinkIcon, {
                    size: 16
                }),
                children: calloutTexts.shareByLink()
            })
        ]
    });
}
export function ResolvePaymentCallout(props) {
    const { invoice, isVendor, reloadInvoice } = props;
    const { stripeUrl, clientSecret } = invoiceModel.processingAction(invoice);
    const { logBI } = useStoreActions();
    const { t: calloutTexts } = useLocaleKeys(calloutTextsPath);
    const { browser: { openNewTab }, reactStripe: { useStripe } } = useIoContext();
    const stripe = useStripe();
    const overlayContext = useOverlayContext();
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const impersonatingAs = useStore(selectors.impersonatingAs);
    async function onResolvePayment(param) {
        let { stripeUrl, clientSecret } = param;
        logBI(10590, {
            invoiceId: invoice.id
        });
        if (stripeUrl != null) {
            openNewTab(stripeUrl);
            document.addEventListener('visibilitychange', listener);
        } else if (stripe != null && clientSecret != null) {
            setLoading(true);
            setErrorMsg('');
            const { error } = await stripe.confirmCardPayment(clientSecret);
            await handlePromise(reloadInvoice());
            if (error) {
                setErrorMsg(error.message || calloutTexts.resolvePaymentFallbackError());
            }
            setLoading(false);
        }
        async function listener() {
            if (document.visibilityState === 'visible') {
                document.removeEventListener('visibilitychange', listener);
                overlayContext.startLoader();
                await handlePromise(reloadInvoice());
                overlayContext.stopLoader();
            }
        }
    }
    return /*#__PURE__*/ _jsx(FullPageInvoiceCallout, {
        id: "payment-verification",
        text: /*#__PURE__*/ _jsxs("div", {
            children: [
                calloutTexts.resolvePaymentCCAuth({
                    party: isVendor ? 'your client' : 'you'
                }),
                errorMsg && /*#__PURE__*/ _jsx(Typography, {
                    testId: "fpi-resolve-payment-error",
                    skin: "error",
                    children: errorMsg
                })
            ]
        }),
        actions: compact([
            (stripeUrl != null || clientSecret != null) && {
                id: 'resolve-payment',
                text: calloutTexts.resolvePayment(),
                intent: Intent.InvoiceResolvePayment,
                loading,
                tooltipTitle: isVendor ? calloutTexts.onlyClientCanComplete() : impersonatingAs ? calloutTexts.disabledWhileImpersonating() : undefined,
                onClick: ()=>onResolvePayment({
                        stripeUrl,
                        clientSecret
                    }),
                disabled: isVendor || !!impersonatingAs
            }
        ])
    });
}
